import React, { useState, useMemo } from 'react';

// Duplicate types from agent.ts to ensure type safety in this module
type MenuItem = {
    id: string;
    name: string;
    category: "Appetizer" | "Main" | "Dessert" | "Beverage";
    price: number;
    cost: number;
    margin: number;
    marginPercent: number;
    ordersPerWeek: number;
    revenueContribution: number;
    menuRole: string; // Simplified for this view
    prepTime: "Low" | "Medium" | "High";
    operationalSignals: string[];
    recommendation: string;
    estimatedImpact: string;
    trend: "up" | "down" | "stable";
};

type Decision = {
    id: string;
    decision: string;
    context: string;
    status: string;
    expectedImpact?: string;
    agentType?: string;
    createdAt?: string;
};

type Metrics = {
    revenue: number;
    cost: number;
    labour: number;
    profit: number;
};

type ScenarioResult = {
    baseline: Metrics;
    adjusted: Metrics;
    insights: string[];
    warnings: string[];
    projectedGraph: { day: string; baseline: number; adjusted: number }[];
};

type Ingredient = {
    id: string;
    name: string;
    cost: number;
    unit: string;
    isCustom?: boolean;
};

const MOCK_INGREDIENTS: Ingredient[] = [
    { id: '1', name: 'Chicken Breast (6oz)', cost: 1.80, unit: 'pcs' },
    { id: '2', name: 'Beef Patty (6oz)', cost: 2.10, unit: 'pcs' },
    { id: '3', name: 'Brioche Bun', cost: 0.40, unit: 'pcs' },
    { id: '4', name: 'Cheddar Slice', cost: 0.20, unit: 'slice' },
    { id: '5', name: 'Lettuce & Tomato', cost: 0.15, unit: 'portion' },
    { id: '6', name: 'House Sauce (2oz)', cost: 0.30, unit: 'portion' },
    { id: '7', name: 'Pesto (2oz)', cost: 0.60, unit: 'portion' },
    { id: '8', name: 'Fries (Side)', cost: 0.45, unit: 'portion' },
];

function FoodSimulator({ menuItems, onSaveMenuItem, onNavigate }: {
    menuItems: MenuItem[],
    onSaveMenuItem: (item: any) => void,
    onNavigate: (v: any) => void
}) {
    const [plate, setPlate] = useState<Ingredient[]>([]);
    const [targetPrice, setTargetPrice] = useState<number>(14.00);
    const [salesForecast, setSalesForecast] = useState<number>(50); // orders per week
    const [newItemName, setNewItemName] = useState("");
    const [selectedLoadId, setSelectedLoadId] = useState("");

    // Ingredient management
    const [availableIngredients, setAvailableIngredients] = useState<Ingredient[]>(MOCK_INGREDIENTS);
    const [showAddIngredient, setShowAddIngredient] = useState(false);
    const [newIng, setNewIng] = useState({ name: "", cost: "0.00", unit: "pcs" });

    const addToPlate = (ing: Ingredient) => setPlate([...plate, { ...ing, id: Math.random().toString() }]); // Unique IDs for plate
    const removeFromPlate = (index: number) => setPlate(plate.filter((_, i) => i !== index));

    const removeAvailableIngredient = (e: React.MouseEvent, id: string) => {
        e.stopPropagation(); // Don't add to plate when deleting
        setAvailableIngredients(prev => prev.filter(ing => ing.id !== id));
    };

    // Calculations
    const plateCost = plate.reduce((acc, ing) => acc + ing.cost, 0);
    const foodCostPercent = targetPrice > 0 ? (plateCost / targetPrice) * 100 : 0;
    const grossProfit = targetPrice - plateCost;

    // Simulation Impact
    const weeklyRevenue = targetPrice * salesForecast;
    const weeklyIngredientSpend = plateCost * salesForecast;
    const prepTimeHours = salesForecast * (3 / 60); // approx 3 mins per order prep impact?
    const laborCostPerHour = 20; // assumed
    const laborImpactCost = prepTimeHours * laborCostPerHour;
    const netCashFlow = weeklyRevenue - weeklyIngredientSpend - laborImpactCost;

    // Actions
    const handleCreateIngredient = () => {
        if (!newIng.name || !newIng.cost) return;
        const ing: Ingredient = {
            id: `custom-${Date.now()}`,
            name: newIng.name,
            cost: parseFloat(newIng.cost),
            unit: newIng.unit,
            isCustom: true
        };
        setAvailableIngredients([...availableIngredients, ing]);
        setNewIng({ name: "", cost: "0.00", unit: "pcs" });
        setShowAddIngredient(false);
    };

    const handleLoadMenuItem = (id: string) => {
        const item = menuItems.find(i => i.id === id);
        if (item) {
            setTargetPrice(item.price);
            setNewItemName(`${item.name} (v2)`);
            // Creates a base ingredient representing the current cost structure
            setPlate([{
                id: 'base-loaded',
                name: `${item.name} Base Cost`,
                cost: item.cost,
                unit: 'portion'
            }]);
            setSelectedLoadId(id);
        }
    };

    const handleSaveToMenu = () => {
        const name = newItemName || "New Simulation Item";
        const newItem = {
            id: `sim-${Date.now()}`,
            name: name,
            category: "Main",
            price: targetPrice,
            cost: plateCost,
            margin: grossProfit,
            marginPercent: foodCostPercent,
            ordersPerWeek: 0, // New item
            revenueContribution: 0,
            menuRole: "Opportunity",
            prepTime: "Medium",
            operationalSignals: ["Simulated"],
            recommendation: "Review forecast",
            estimatedImpact: "Pending",
            trend: "stable"
        };
        onSaveMenuItem(newItem);
        alert(`Saved "${name}" to Menu Intelligence!`);
        onNavigate("menu");
    };

    return React.createElement("div", { className: "space-y-8 animate-in fade-in duration-500" }, [

        // Control Bar (Load / Add)
        React.createElement("div", { className: "relative overflow-hidden flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm w-full max-w-4xl mx-auto" }, [
            React.createElement("div", { className: "absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-600 via-sky-400 to-emerald-300" }),
            React.createElement("div", { className: "flex flex-wrap items-center gap-3" }, [
                React.createElement("div", { className: "inline-flex items-center gap-3 bg-slate-50 border border-slate-200/80 rounded-xl px-3 py-2" }, [
                    React.createElement("span", { className: "text-[10px] font-black text-slate-400 uppercase tracking-widest" }, "Load Existing Item"),
                    React.createElement("select", {
                        value: selectedLoadId,
                        onChange: (e: any) => handleLoadMenuItem(e.target.value),
                        className: "bg-white border border-slate-200 rounded-xl text-sm px-3 py-2 font-semibold text-slate-700 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 w-44 max-w-full"
                    }, [
                        React.createElement("option", { key: "default", value: "" }, "Select item..."),
                        ...menuItems.map(m => React.createElement("option", { key: m.id, value: m.id }, m.name))
                    ])
                ])
            ]),
            React.createElement("button", {
                onClick: () => setShowAddIngredient(!showAddIngredient),
                className: "text-[10px] font-black uppercase tracking-widest text-indigo-600 hover:bg-indigo-50 px-4 py-2 rounded-xl transition-colors border border-indigo-100"
            }, showAddIngredient ? "Cancel" : "+ Create Custom Ingredient")
        ]),

        // Custom Ingredient Form
        showAddIngredient && React.createElement("div", { className: "relative overflow-hidden bg-white p-4 rounded-2xl border border-indigo-100 flex flex-col md:flex-row md:items-end gap-4 animate-in slide-in-from-top-2 shadow-sm w-full max-w-4xl mx-auto" }, [
            React.createElement("div", { className: "absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-600 via-indigo-400 to-sky-300" }),
            React.createElement("div", { className: "flex-1" }, [
                React.createElement("label", { className: "block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2" }, "Ingredient Name"),
                React.createElement("input", {
                    value: newIng.name,
                    onChange: (e: any) => setNewIng({ ...newIng, name: e.target.value }),
                    className: "w-full p-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 focus:ring-2 focus:ring-indigo-500/10",
                    placeholder: "e.g. Truffle Oil"
                })
            ]),
            React.createElement("div", { className: "w-24" }, [
                React.createElement("label", { className: "block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2" }, "Cost (£)"),
                React.createElement("input", {
                    type: "number",
                    value: newIng.cost,
                    onChange: (e: any) => setNewIng({ ...newIng, cost: e.target.value }),
                    className: "w-full p-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 focus:ring-2 focus:ring-indigo-500/10"
                })
            ]),
            React.createElement("div", { className: "w-24" }, [
                React.createElement("label", { className: "block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2" }, "Unit"),
                React.createElement("select", {
                    value: newIng.unit,
                    onChange: (e: any) => setNewIng({ ...newIng, unit: e.target.value }),
                    className: "w-full p-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 focus:ring-2 focus:ring-indigo-500/10"
                }, ["pcs", "portion", "slice", "kg", "lit"].map(u => React.createElement("option", { key: u, value: u }, u)))
            ]),
            React.createElement("button", {
                onClick: handleCreateIngredient,
                className: "bg-indigo-600 text-white font-black text-[10px] uppercase tracking-widest px-4 py-2 rounded-xl hover:bg-indigo-700"
            }, "Add")
        ]),

        // Step 1: The Build
        React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-8" }, [
            // Ingredients List
            React.createElement("div", { className: "relative overflow-hidden bg-white p-6 rounded-2xl border border-slate-200 shadow-sm" }, [
                React.createElement("div", { className: "absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-600 via-sky-400 to-emerald-300" }),
                React.createElement("h3", { className: "font-black text-slate-900 mb-4 text-sm uppercase tracking-widest" }, "Ingredient Library"),
                React.createElement("div", { className: "grid grid-cols-2 gap-2 max-h-96 overflow-y-auto pr-2" },
                    availableIngredients.map(ing =>
                        React.createElement("div", {
                            key: ing.id,
                            onClick: () => addToPlate(ing),
                            className: "p-3 text-left border border-slate-100 rounded-xl hover:bg-indigo-50 hover:border-indigo-200 transition-all group cursor-pointer relative bg-white"
                        }, [
                            React.createElement("div", { className: "font-bold text-slate-700 text-sm group-hover:text-indigo-700 truncate pr-6" }, ing.name),
                            React.createElement("div", { className: "text-[10px] text-slate-400 font-mono" }, `£${ing.cost.toFixed(2)} / ${ing.unit}`),
                            React.createElement("button", {
                                onClick: (e: any) => removeAvailableIngredient(e, ing.id),
                                className: "absolute top-2 right-2 p-1 opacity-0 group-hover:opacity-100 hover:bg-red-50 text-red-400 rounded transition-all",
                                title: "Remove from library"
                            }, "×")
                        ])
                    )
                )
            ]),

            // Virtual Plate Output
            React.createElement("div", { className: "relative overflow-hidden bg-white/80 p-6 rounded-2xl border border-slate-200 flex flex-col justify-between" }, [
                React.createElement("div", { className: "absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-slate-300 via-slate-200 to-slate-100" }),
                React.createElement("div", {}, [
                    React.createElement("h4", { className: "text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4" }, "Virtual Plate Content"),
                    plate.length === 0
                        ? React.createElement("div", { className: "text-center py-10 text-slate-400 italic border border-dashed border-slate-200 rounded-xl" }, "Plate is empty... add ingredients")
                        : React.createElement("div", { className: "space-y-2" },
                            plate.map((ing, idx) =>
                                React.createElement("div", { key: idx, className: "flex justify-between items-center bg-white p-2 rounded-lg shadow-sm border border-slate-100" }, [
                                    React.createElement("span", { className: "text-sm font-bold text-slate-700" }, ing.name),
                                    React.createElement("div", { className: "flex items-center gap-3" }, [
                                        React.createElement("span", { className: "text-xs font-mono text-slate-500 font-bold" }, `£${ing.cost.toFixed(2)}`),
                                        React.createElement("button", {
                                            onClick: () => removeFromPlate(idx),
                                            className: "w-6 h-6 flex items-center justify-center bg-red-50 text-red-500 hover:bg-red-100 rounded-full transition-colors text-lg"
                                        }, "×")
                                    ])
                                ])
                            )
                        )
                ]),
                React.createElement("div", { className: "mt-6 pt-4 border-t border-slate-200 flex justify-between items-end" }, [
                    React.createElement("span", { className: "font-black text-slate-500 text-[10px] uppercase tracking-widest" }, "Total Plate Cost"),
                    React.createElement("span", { className: "text-3xl font-black text-slate-900" }, `£${plateCost.toFixed(2)}`)
                ])
            ])
        ]),

        // Step 2: Strategy (Pricing)
        React.createElement("div", { className: "relative overflow-hidden bg-white p-6 rounded-2xl border border-slate-200 shadow-sm" }, [
            React.createElement("div", { className: "absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-600 via-sky-400 to-emerald-300" }),
            React.createElement("h3", { className: "font-black text-slate-900 mb-6 text-sm uppercase tracking-widest" }, "Pricing Strategy"),
            React.createElement("div", { className: "flex flex-col md:flex-row items-end gap-8" }, [

                // Item Name Input (New)
                React.createElement("div", { className: "w-full md:w-1/3" }, [
                    React.createElement("label", { className: "block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2" }, "Menu Item Name"),
                    React.createElement("input", {
                        value: newItemName,
                        onChange: (e: any) => setNewItemName(e.target.value),
                        placeholder: "e.g. Summer Special Burger",
                        className: "w-full p-3 bg-white border border-slate-200 rounded-xl font-semibold text-slate-900 focus:ring-2 focus:ring-indigo-500/10 outline-none"
                    })
                ]),

                // Price Input
                React.createElement("div", { className: "w-full md:w-1/3" }, [
                    React.createElement("label", { className: "block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2" }, "Target Menu Price"),
                    React.createElement("div", { className: "relative" }, [
                        React.createElement("span", { className: "absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-bold" }, "£"),
                        React.createElement("input", {
                            type: "text",
                            inputMode: "decimal",
                            pattern: "[0-9]*",
                            value: targetPrice,
                            onChange: (e: any) => setTargetPrice(parseFloat(e.target.value) || 0),
                            className: "w-full pl-8 pr-4 py-3 bg-white border border-slate-200 rounded-xl font-black text-xl text-slate-900 focus:ring-2 focus:ring-indigo-500/10 outline-none"
                        })
                    ])
                ]),

                // KPI Cards
                React.createElement("div", { className: "grid grid-cols-2 gap-4 flex-1 w-full" }, [
                    React.createElement("div", { className: `p-4 rounded-xl border ${foodCostPercent > 30 ? 'bg-rose-50 border-rose-100 text-rose-700' : 'bg-emerald-50 border-emerald-100 text-emerald-700'}` }, [
                        React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest opacity-70 mb-1" }, "Food Cost %"),
                        React.createElement("p", { className: "text-2xl font-black" }, `${foodCostPercent.toFixed(1)}%`)
                    ]),
                    React.createElement("div", { className: "p-4 rounded-xl border bg-indigo-50 border-indigo-100 text-indigo-700" }, [
                        React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest opacity-70 mb-1" }, "Gross Profit"),
                        React.createElement("p", { className: "text-2xl font-black" }, `£${grossProfit.toFixed(2)}`)
                    ])
                ])
            ])
        ]),

        // Step 3: Simulation (Impact)
        React.createElement("div", { className: "bg-gradient-to-br from-gray-900 to-indigo-900 p-8 rounded-3xl shadow-xl text-white" }, [
            React.createElement("div", { className: "flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4" }, [
                React.createElement("h3", { className: "font-black text-xl" }, "Cash Flow Impact"),
                React.createElement("div", { className: "flex items-center gap-3 bg-white/10 px-4 py-2 rounded-xl" }, [
                    React.createElement("span", { className: "text-sm font-bold text-indigo-200" }, "Forecast Orders/Week:"),
                    React.createElement("input", {
                        type: "text",
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                        value: salesForecast,
                        onChange: (e: any) => setSalesForecast(parseFloat(e.target.value) || 0),
                        className: "w-20 bg-transparent border-b border-indigo-300 font-black text-center text-white focus:outline-none"
                    })
                ])
            ]),

            React.createElement("div", { className: "grid grid-cols-2 md:grid-cols-4 gap-6 mb-8" }, [
                React.createElement("div", {}, [
                    React.createElement("p", { className: "text-xs font-bold text-indigo-300 uppercase mb-1" }, "Revenue Increase"),
                    React.createElement("p", { className: "text-2xl font-black text-green-400" }, `+£${weeklyRevenue.toFixed(0)}`)
                ]),
                React.createElement("div", {}, [
                    React.createElement("p", { className: "text-xs font-bold text-indigo-300 uppercase mb-1" }, "Ingredient Spend"),
                    React.createElement("p", { className: "text-2xl font-black text-red-300" }, `-£${weeklyIngredientSpend.toFixed(0)}`)
                ]),
                React.createElement("div", {}, [
                    React.createElement("p", { className: "text-xs font-bold text-indigo-300 uppercase mb-1" }, "Labor Impact (Est.)"),
                    React.createElement("p", { className: "text-2xl font-black text-orange-300" }, `-£${laborImpactCost.toFixed(0)}`)
                ]),
                React.createElement("div", { className: "bg-white/10 p-4 rounded-xl border border-white/10" }, [
                    React.createElement("p", { className: "text-xs font-bold text-white uppercase mb-1" }, "Net Weekly Cash Flow"),
                    React.createElement("p", { className: "text-2xl font-black text-white" }, `${netCashFlow > 0 ? '+' : ''}£${netCashFlow.toFixed(0)}`)
                ])
            ]),

            React.createElement("button", {
                onClick: handleSaveToMenu,
                className: "w-full max-w-md mx-auto py-4 bg-white text-indigo-900 font-black text-lg rounded-2xl hover:bg-gray-100 transition-all shadow-lg flex items-center justify-center gap-2"
            }, `Save "${newItemName || 'Item'}" to Menu Intelligence`)
        ])
    ]);
}

export function ScenarioView({ decisions, menuItems, onSaveMenuItem, onNavigate }: {
    decisions: Decision[],
    menuItems: MenuItem[],
    onSaveMenuItem: (item: any) => void,
    onNavigate: (v: any) => void
}) {
    const [subTab, setSubTab] = useState<'horizon' | 'food'>('food');

    // Horizon Scanning Logic (Existing)
    const [selectedDecisionId, setSelectedDecisionId] = useState<string | null>(null);
    const [mode, setMode] = useState<'select' | 'simulate'>('select');
    const [isSimulating, setIsSimulating] = useState(false);

    // Variables for Fine-Tuning
    const [variables, setVariables] = useState({
        staffAdj: 0,
        weather: "Historical Avg",
        events: "None",
        economy: "Stable",
        spending: "Normal",
        occasion: "None",
        holiday: "None"
    });

    const [scenarioLog, setScenarioLog] = useState<{ id: string; decisionId: string; date: string; result: ScenarioResult }[]>([]);

    const selectedDecision = decisions.find(d => d.id === selectedDecisionId);

    const calculateResults = useMemo(() => {
        if (!selectedDecision) return null;

        const baseRev = 5000;
        const baseCost = 1500;
        const baseLabour = 1200;

        let revMod = 1.0;
        let costMod = 1.0;
        let labourMod = 1.0;

        labourMod += (variables.staffAdj * 0.05);
        if (variables.weather === "Sunny") revMod += 0.15;
        if (variables.weather === "Rain") revMod -= 0.10;
        if (variables.weather === "Storm") revMod -= 0.25;
        if (variables.events === "Local Game") { revMod += 0.20; labourMod += 0.10; }
        if (variables.events === "Concert") { revMod += 0.30; labourMod += 0.15; }
        if (variables.economy === "Recession") revMod -= 0.15;
        if (variables.economy === "Boom") revMod += 0.10;
        if (variables.spending === "Conservative") revMod -= 0.05;
        if (variables.spending === "Splurge") revMod += 0.10;
        if (variables.occasion === "Valentines") { revMod += 0.25; costMod += 0.05; }
        if (variables.occasion === "Xmas") { revMod += 0.40; labourMod += 0.20; }
        if (variables.occasion === "NYE") { revMod += 0.50; labourMod += 0.30; }
        if (variables.holiday === "Bank Holiday") { revMod += 0.20; labourMod += 0.10; }

        const baseline: Metrics = {
            revenue: baseRev,
            cost: baseCost,
            labour: baseLabour,
            profit: baseRev - baseCost - baseLabour
        };

        const adjusted: Metrics = {
            revenue: Math.round(baseRev * revMod),
            cost: Math.round(baseCost * costMod),
            labour: Math.round(baseLabour * labourMod),
            profit: Math.round((baseRev * revMod) - (baseCost * costMod) - (baseLabour * labourMod))
        };

        const projectedGraph = Array.from({ length: 7 }).map((_, i) => ({
            day: `Day ${i + 1}`,
            baseline: Math.round(baseRev * (0.8 + Math.random() * 0.4)),
            adjusted: Math.round(baseRev * revMod * (0.8 + Math.random() * 0.4))
        }));

        const insights = [];
        const warnings = [];

        if (adjusted.profit > baseline.profit * 1.1) insights.push("Significant profit boost expected.");
        if (variables.weather === "Rain" && variables.events !== "None") warnings.push("Rain may dampen event impact.");
        if (variables.staffAdj < 0 && revMod > 1.2) warnings.push("Risk of understaffing during high demand.");
        if (adjusted.labour > adjusted.revenue * 0.35) warnings.push("Labour costs exceeding 35% of revenue.");

        if (insights.length === 0) insights.push("Performance remains stable within limits.");

        return { baseline, adjusted, insights, warnings, projectedGraph };

    }, [selectedDecision, variables]);

    const handleRun = () => {
        setIsSimulating(true);
        setTimeout(() => {
            setIsSimulating(false);
            setMode('simulate');
        }, 1500);
    };

    const handleSaveLog = () => {
        if (calculateResults && selectedDecision) {
            setScenarioLog(prev => [{
                id: Date.now().toString(),
                decisionId: selectedDecision.id,
                date: new Date().toISOString(),
                result: calculateResults
            }, ...prev]);
            alert("Scenario saved to log.");
        }
    };

    const MetricCard = ({ label, base, adj, prefix = "£" }: any) => {
        const diff = adj - base;
        const isPositive = diff > 0;
        const color = label === "Cost" || label === "Labour"
            ? (isPositive ? "text-red-600" : "text-green-600")
            : (isPositive ? "text-green-600" : "text-red-600");

        return React.createElement("div", { className: "bg-white p-4 rounded-xl border border-gray-100 shadow-sm" }, [
            React.createElement("p", { className: "text-xs font-bold text-gray-400 uppercase tracking-widest mb-1" }, label),
            React.createElement("div", { className: "flex items-end gap-2" }, [
                React.createElement("span", { className: "text-2xl font-black text-gray-900" }, `${prefix}${adj.toLocaleString()}`),
                React.createElement("span", { className: `text-xs font-bold ${color} mb-1 bg-gray-50 px-1 rounded` },
                    `${diff > 0 ? '+' : ''}${prefix}${diff.toLocaleString()}`
                )
            ]),
            React.createElement("p", { className: "text-[10px] text-gray-400 mt-1" }, `Baseline: ${prefix}${base.toLocaleString()}`)
        ]);
    };

    const VariableSelect = ({ label, value, options, onChange }: any) => (
        React.createElement("div", { className: "space-y-1" }, [
            React.createElement("label", { className: "text-[10px] font-bold text-gray-500 uppercase" }, label),
            React.createElement("select", {
                value: value,
                onChange: (e: any) => onChange(e.target.value),
                className: "w-full p-2 bg-gray-50 border border-gray-200 rounded-lg text-xs font-bold text-gray-700 outline-none focus:border-indigo-500 hover:bg-white transition-colors"
            }, options.map((opt: string) =>
                React.createElement("option", { key: opt, value: opt }, opt)
            ))
        ])
    );

    return React.createElement("div", { className: "space-y-8 animate-in fade-in duration-500 pb-12" }, [
        // Page Title & Header
        React.createElement("div", { key: "header-main", className: "mb-8 bg-gradient-to-br from-indigo-50 to-white p-8 rounded-[32px] border border-indigo-100/50 shadow-sm" }, [
            React.createElement("h1", { className: "text-3xl font-black text-gray-900 tracking-tight mb-2" }, "Simulation & Horizon Scanning"),
            React.createElement("p", { className: "text-gray-500 font-medium max-w-3xl leading-relaxed" },
                "Optimize your menu profitability and test operational decisions in a sandbox environment. Use Horizon Scanning to predict how weather, local events, and macro-economic factors will impact your future revenue and costs."
            )
        ]),

        // Tab Headers
        React.createElement("div", { className: "flex gap-4 mb-6 border-b border-gray-200" }, [
            React.createElement("button", {
                onClick: () => setSubTab('food'),
                className: `pb-3 px-1 text-sm font-bold transition-all border-b-2 ${subTab === 'food' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`
            }, "Food Cost Simulator"),
            React.createElement("button", {
                onClick: () => setSubTab('horizon'),
                className: `pb-3 px-1 text-sm font-bold transition-all border-b-2 ${subTab === 'horizon' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`
            }, "Action Simulator"),
        ]),

        // Content
        subTab === 'food' ? React.createElement(FoodSimulator, {
            menuItems,
            onSaveMenuItem,
            onNavigate
        }) :

            React.createElement("div", { className: "space-y-6 animate-in fade-in duration-500" }, [
                // Header
                React.createElement("div", { className: "flex justify-between items-center bg-white p-6 rounded-2xl border border-gray-200 shadow-sm" }, [
                    React.createElement("div", {}, [
                React.createElement("div", { className: "flex items-center gap-2 mb-1" }, [
                    React.createElement("h2", { className: "text-2xl font-black text-gray-900" }, "Action Simulator")
                ]),
                        React.createElement("p", { className: "text-sm text-gray-500 font-medium ml-13" }, "Simulate Action Tracker decisions against future variables.")
                    ]),
                    mode === 'simulate' && React.createElement("button", {
                        onClick: () => {
                            setMode('select'); setVariables({
                                staffAdj: 0, weather: "Historical Avg", events: "None", economy: "Stable", spending: "Normal", occasion: "None", holiday: "None"
                            });
                        },
                        className: "text-xs font-bold text-gray-400 hover:text-gray-900 underline"
                    }, "Start New Simulation")
                ]),

                // MODE: SELECT
                mode === 'select' && React.createElement("div", { className: "grid grid-cols-1 lg:grid-cols-2 gap-8" }, [
                    // Left: Decision List
                    React.createElement("div", { className: "space-y-4" }, [
                        React.createElement("h3", { className: "text-sm font-black text-gray-400 uppercase tracking-widest pl-1" }, "Select Decision to Test"),
                        decisions.length === 0 ?
                            React.createElement("div", { className: "p-8 text-center bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 text-gray-400" }, "No decisions in Action Tracker.") :
                            decisions.map(d =>
                                React.createElement("div", {
                                    key: d.id,
                                    onClick: () => setSelectedDecisionId(d.id),
                                    className: `p-5 rounded-xl border cursor-pointer transition-all group ${selectedDecisionId === d.id
                                        ? 'bg-indigo-50 border-indigo-600 shadow-md ring-1 ring-indigo-600'
                                        : 'bg-white border-gray-200 hover:border-indigo-300 hover:bg-gray-50'}`
                                }, [
                                    React.createElement("div", { className: "flex justify-between items-start mb-2" }, [
                                        React.createElement("span", { className: `px-2 py-0.5 rounded text-[10px] font-black uppercase ${d.agentType === 'chef' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}` }, d.agentType || 'System'),
                                        React.createElement("span", { className: "text-[10px] text-gray-400 font-mono" }, new Date(d.createdAt || Date.now()).toLocaleDateString())
                                    ]),
                                    React.createElement("p", { className: "text-sm font-bold text-gray-900 mb-1" }, d.decision),
                                    React.createElement("p", { className: "text-xs text-gray-500 line-clamp-2" }, d.context)
                                ])
                            )
                    ]),

                    // Right: Preview & Action
                    React.createElement("div", { className: "flex flex-col justify-center items-center p-8 bg-gray-50 rounded-3xl border border-gray-200 text-center space-y-6" },
                        selectedDecision ? [
                            React.createElement("div", { className: "w-20 h-20 bg-white rounded-full flex items-center justify-center text-xs font-black text-gray-700 shadow-sm mb-2 uppercase tracking-widest" }, "Run"),
                            React.createElement("h3", { className: "text-xl font-bold text-gray-900" }, "Ready to Simulate"),
                            React.createElement("p", { className: "text-sm text-gray-500 max-w-xs" }, "Run a baseline simulation for this decision using current historical data."),
                            React.createElement("button", {
                                onClick: handleRun,
                                className: "px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold shadow-lg shadow-indigo-200 hover:scale-105 transition-all w-full max-w-xs flex items-center justify-center gap-2"
                            }, isSimulating ? "Running..." : "Run Base Simulation")
                        ] : [
                            React.createElement("div", { className: "w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center text-[10px] font-black text-gray-500 uppercase tracking-widest opacity-80 mb-2" }, "Select"),
                            React.createElement("p", { className: "text-gray-400 font-medium" }, "Select a decision from the list to begin.")
                        ]
                    )
                ]),

                // MODE: SIMULATE (Results & Fine-tune)
                mode === 'simulate' && calculateResults && React.createElement("div", { className: "grid grid-cols-1 lg:grid-cols-12 gap-6" }, [
                    // Left: Fine Tuning Panel (Col 3)
                    React.createElement("div", { className: "lg:col-span-3 space-y-4" }, [
                        React.createElement("div", { className: "bg-white p-5 rounded-2xl border border-gray-200 shadow-sm" }, [
                            React.createElement("div", { className: "mb-4 border-b border-gray-100 pb-3" }, [
                                React.createElement("h3", { className: "font-black text-xs text-gray-900 uppercase tracking-wide" }, "Fine-Tune Scenario")
                            ]),
                            React.createElement("div", { className: "space-y-4" }, [
                                React.createElement("div", {}, [
                                    React.createElement("label", { className: "text-[10px] font-bold text-gray-500 uppercase flex justify-between" },
                                        ["Additional Staff", React.createElement("span", { className: "text-indigo-600" }, variables.staffAdj > 0 ? `+${variables.staffAdj}` : variables.staffAdj)]
                                    ),
                                    React.createElement("input", {
                                        type: "range", min: "-2", max: "5", step: "1",
                                        value: variables.staffAdj,
                                        onChange: (e: any) => setVariables({ ...variables, staffAdj: parseInt(e.target.value) }),
                                        className: "w-full mt-2 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                                    })
                                ]),
                                React.createElement(VariableSelect, { label: "Weather", value: variables.weather, options: ["Historical Avg", "Sunny", "Rain", "Storm"], onChange: (v: string) => setVariables({ ...variables, weather: v }) }),
                                React.createElement(VariableSelect, { label: "Local Events", value: variables.events, options: ["None", "Local Game", "Concert"], onChange: (v: string) => setVariables({ ...variables, events: v }) }),
                                React.createElement(VariableSelect, { label: "Economy", value: variables.economy, options: ["Stable", "Recession", "Boom"], onChange: (v: string) => setVariables({ ...variables, economy: v }) }),
                                React.createElement(VariableSelect, { label: "Spending Habits", value: variables.spending, options: ["Normal", "Conservative", "Splurge"], onChange: (v: string) => setVariables({ ...variables, spending: v }) }),
                                React.createElement(VariableSelect, { label: "Special Occasion", value: variables.occasion, options: ["None", "Valentines", "Xmas", "NYE"], onChange: (v: string) => setVariables({ ...variables, occasion: v }) }),
                                React.createElement(VariableSelect, { label: "Holiday", value: variables.holiday, options: ["None", "School", "Bank Holiday"], onChange: (v: string) => setVariables({ ...variables, holiday: v }) }),
                            ])
                        ]),
                        React.createElement("div", { className: "bg-indigo-900 p-5 rounded-2xl text-white space-y-3" }, [
                            React.createElement("h4", { className: "font-black text-xs uppercase opacity-70" }, "Decision Actions"),
                            React.createElement("button", { onClick: handleSaveLog, className: "w-full py-2 bg-white/10 hover:bg-white/20 rounded-lg text-xs font-bold transition-all border border-white/10 text-center" }, "Save Scenario to Log"),
                            React.createElement("button", { onClick: () => { alert("Decision updated!"); onNavigate('decisions'); }, className: "w-full py-2 bg-white text-indigo-900 rounded-lg text-xs font-bold hover:bg-indigo-50 transition-all text-center" }, "Update Action Tracker")
                        ])
                    ]),

                    // Right: Results & Viz (Col 9)
                    React.createElement("div", { className: "lg:col-span-9 space-y-6" }, [
                        React.createElement("div", { className: "grid grid-cols-2 md:grid-cols-4 gap-4" }, [
                            React.createElement(MetricCard, { label: "Revenue", base: calculateResults.baseline.revenue, adj: calculateResults.adjusted.revenue }),
                            React.createElement(MetricCard, { label: "Cost", base: calculateResults.baseline.cost, adj: calculateResults.adjusted.cost }),
                            React.createElement(MetricCard, { label: "Labour", base: calculateResults.baseline.labour, adj: calculateResults.adjusted.labour }),
                            React.createElement(MetricCard, { label: "Profit", base: calculateResults.baseline.profit, adj: calculateResults.adjusted.profit }),
                        ]),
                        React.createElement("div", { className: "bg-white p-6 rounded-2xl border border-gray-200 shadow-sm" }, [
                            React.createElement("h3", { className: "font-bold text-gray-900 mb-6" }, "Projected Revenue: Baseline vs Adjusted Scenario"),
                            React.createElement("div", { className: "h-64 flex items-end justify-between px-2 gap-4 border-b border-gray-100" },
                                calculateResults.projectedGraph.map((d, i) => {
                                    const max = Math.max(...calculateResults.projectedGraph.map(p => Math.max(p.baseline, p.adjusted))) * 1.2;
                                    return React.createElement("div", { key: i, className: "flex-1 flex flex-col justify-end h-full group relative" }, [
                                        React.createElement("div", { className: "absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[10px] p-2 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none" },
                                            `Base: £${d.baseline.toLocaleString()} | Adj: £${d.adjusted.toLocaleString()}`
                                        ),
                                        React.createElement("div", { className: "flex items-end justify-center gap-1 h-full w-full pb-1" }, [
                                            React.createElement("div", { className: "flex-1 bg-gray-200 rounded-t-sm opacity-60 hover:opacity-100 transition-all", style: { height: `${(d.baseline / max) * 100}%`, minHeight: '2px' } }),
                                            React.createElement("div", { className: "flex-1 bg-indigo-500 rounded-t-sm hover:brightness-110 transition-all shadow-sm", style: { height: `${(d.adjusted / max) * 100}%`, minHeight: '2px' } })
                                        ]),
                                        React.createElement("span", { className: "text-[10px] font-bold text-gray-400 text-center uppercase mt-1 mb-2" }, d.day)
                                    ]);
                                })
                            ),
                            React.createElement("div", { className: "flex justify-center gap-6 mt-6" }, [
                                React.createElement("div", { className: "flex items-center gap-2" }, [
                                    React.createElement("div", { className: "w-3 h-3 bg-gray-300 rounded-full" }),
                                    React.createElement("span", { className: "text-xs font-bold text-gray-500" }, "Baseline (Current Data)")
                                ]),
                                React.createElement("div", { className: "flex items-center gap-2" }, [
                                    React.createElement("div", { className: "w-3 h-3 bg-indigo-600 rounded-full" }),
                                    React.createElement("span", { className: "text-xs font-bold text-gray-900" }, "Adjusted Scenario")
                                ])
                            ])
                        ]),
                        React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4" }, [
                            React.createElement("div", { className: "bg-gradient-to-br from-indigo-50 to-white p-6 rounded-2xl border border-indigo-100" }, [
                                React.createElement("h4", { className: "font-black text-indigo-900 text-sm uppercase tracking-wide mb-4" }, "AI Recommendations"),
                                React.createElement("ul", { className: "space-y-3" }, calculateResults.insights.map((insight, i) => React.createElement("li", { key: i, className: "flex gap-3 text-sm text-indigo-800 font-medium" }, [React.createElement("span", { className: "font-bold" }, "•"), insight])))
                            ]),
                            React.createElement("div", { className: "bg-gradient-to-br from-orange-50 to-white p-6 rounded-2xl border border-orange-100" }, [
                                React.createElement("h4", { className: "font-black text-orange-900 text-sm uppercase tracking-wide mb-4" }, "Risk Warnings"),
                                calculateResults.warnings.length === 0
                                    ? React.createElement("p", { className: "text-sm text-orange-800/60 italic" }, "No critical risks detected.")
                                    : React.createElement("ul", { className: "space-y-3" }, calculateResults.warnings.map((w, i) => React.createElement("li", { key: i, className: "flex gap-3 text-sm text-orange-800 font-medium" }, [React.createElement("span", { className: "font-bold" }, "!"), w])))
                            ])
                        ])
                    ])
                ]),

                // Scenario Log at bottom
                scenarioLog.length > 0 && mode === 'simulate' && React.createElement("div", { className: "mt-12 pt-8 border-t border-gray-200" }, [
                    React.createElement("h3", { className: "text-lg font-black text-gray-900 mb-4" }, "Scenario Log"),
                    React.createElement("div", { className: "flex gap-4 overflow-x-auto pb-4" }, scenarioLog.map(log =>
                        React.createElement("div", { key: log.id, className: "min-w-[200px] bg-white p-4 rounded-xl border border-gray-200 shadow-sm" }, [
                            React.createElement("p", { className: "text-[10px] text-gray-400 font-mono mb-1" }, new Date(log.date).toLocaleDateString()),
                            React.createElement("p", { className: "text-xs font-bold text-indigo-600 mb-2" }, `Profit: £${log.result.adjusted.profit.toLocaleString()}`),
                            React.createElement("div", { className: "text-[10px] text-gray-500 space-y-1" }, [
                                React.createElement("p", {}, `vs Base: £${log.result.baseline.profit.toLocaleString()}`)
                            ])
                        ])
                    ))
                ])
            ])
    ]);
}
